/**
 * Centralises URL-synced server-table state for the dynamic exception
 * route. Replaces the previous ~80 lines of inline useMemo / useEffect
 * pairs that handled `?page=`, `?pageSize=`, and `?search=` mirroring
 * with debounce + hydrate.
 *
 * Returns:
 * - `urlState` - the current snapshot fed to `buildFetchParams` and
 *   `emptyMessage` callbacks. Includes the raw `searchParams` so
 *   per-config filters can read their own custom axes (issue, owner,
 *   status, mismatch, etc).
 * - `searchInput` / `setSearchInput` - locally-owned input value so
 *   keystrokes don't write to the URL every character. Debounced into
 *   `?search=` after 400ms.
 * - `setPage` / `setPageSize` - URL-writing setters that omit defaults
 *   (e.g. page=0 -> param removed) so URLs stay clean.
 *
 * No-op when `config.serverTable` is undefined: defaults to page 0,
 * the supplied default page size, empty search, and no-op setters.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from './useDebounce'
import type {
  ExceptionConfig,
  ExceptionUrlState,
} from '../config/exceptions/types'

interface UseExceptionUrlStateOptions {
  config: ExceptionConfig | null
  searchParams: URLSearchParams
  setSearchParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void
}

interface UseExceptionUrlStateReturn {
  urlState: ExceptionUrlState
  searchInput: string
  setSearchInput: (value: string) => void
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
  serverTableEnabled: boolean
  searchEnabled: boolean
  /**
   * `?exceptionObject=<object_id>` URL param drives the exception
   * drawer's open row, so deep-links and the browser back button work
   * the same way pagination + filters already do. `null` when absent.
   *
   * Param name is intentionally scoped (`exceptionObject` rather than
   * a generic `object` or `drawer`) so future drawer-bearing surfaces
   * on adjacent URL paths can use their own scoped names without
   * collision (e.g. an admin user drawer would use its own `?adminUser=`).
   */
  exceptionObject: string | null
  setExceptionObject: (next: string | null) => void
}

const SEARCH_DEBOUNCE_MS = 400

export function useExceptionUrlState({
  config,
  searchParams,
  setSearchParams,
}: UseExceptionUrlStateOptions): UseExceptionUrlStateReturn {
  const serverTable = config?.serverTable ?? null
  const serverTableEnabled = serverTable !== null
  const searchEnabled = serverTableEnabled && (serverTable?.enableSearch ?? true)
  const defaultPageSize = serverTable?.defaultPageSize ?? 50
  const pageSizeOptions = serverTable?.pageSizeOptions ?? [defaultPageSize]

  const page = useMemo<number>(() => {
    if (!serverTableEnabled) return 0
    const raw = parseInt(searchParams.get('page') ?? '', 10)
    return Number.isFinite(raw) && raw >= 0 ? raw : 0
  }, [searchParams, serverTableEnabled])

  const pageSize = useMemo<number>(() => {
    if (!serverTableEnabled) return defaultPageSize
    const raw = parseInt(searchParams.get('pageSize') ?? '', 10)
    return pageSizeOptions.includes(raw) ? raw : defaultPageSize
  }, [searchParams, serverTableEnabled, defaultPageSize, pageSizeOptions])

  const urlSearchValue = useMemo<string>(() => {
    if (!searchEnabled) return ''
    return (searchParams.get('search') ?? '').trim()
  }, [searchParams, searchEnabled])

  // Local search input so keystrokes don't write to the URL every char.
  // Debounced value mirrors into `?search=` and pages back to 0 on each
  // change. Hydrate from URL when it changes externally (back/forward).
  const [searchInput, setSearchInput] = useState<string>(
    () => searchParams.get('search') ?? '',
  )
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    if (!searchEnabled) return
    const trimmed = debouncedSearch.trim()
    const current = (searchParams.get('search') ?? '').trim()
    if (trimmed === current) return
    const next = new URLSearchParams(searchParams)
    if (trimmed) next.set('search', trimmed)
    else next.delete('search')
    next.delete('page')
    setSearchParams(next, { replace: true })
    // searchParams intentionally omitted: the URL we just wrote would
    // re-trigger this effect and the equality guard would no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, searchEnabled])

  useEffect(() => {
    if (!searchEnabled) return
    const urlSearch = searchParams.get('search') ?? ''
    if (urlSearch === debouncedSearch.trim()) return
    if (urlSearch !== searchInput) setSearchInput(urlSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, searchEnabled])

  const setPage = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams)
      if (nextPage === 0) next.delete('page')
      else next.set('page', String(nextPage))
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const setPageSize = useCallback(
    (nextSize: number) => {
      const next = new URLSearchParams(searchParams)
      if (nextSize === defaultPageSize) next.delete('pageSize')
      else next.set('pageSize', String(nextSize))
      next.delete('page')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams, defaultPageSize],
  )

  const exceptionObject = useMemo<string | null>(() => {
    const raw = searchParams.get('exceptionObject')
    if (!raw) return null
    const trimmed = raw.trim()
    return trimmed === '' ? null : trimmed
  }, [searchParams])

  const setExceptionObject = useCallback(
    (nextObject: string | null) => {
      const next = new URLSearchParams(searchParams)
      // Strip the pre-rename `?object=` alias so URLs that arrived from
      // a stale bookmark or external link don't accumulate both shapes.
      next.delete('object')
      const trimmed = nextObject?.trim() || ''
      if (trimmed === '') next.delete('exceptionObject')
      else next.set('exceptionObject', trimmed)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const urlState = useMemo<ExceptionUrlState>(
    () => ({
      page,
      pageSize,
      search: urlSearchValue,
      searchParams,
    }),
    [page, pageSize, urlSearchValue, searchParams],
  )

  return {
    urlState,
    searchInput,
    setSearchInput,
    setPage,
    setPageSize,
    serverTableEnabled,
    searchEnabled,
    exceptionObject,
    setExceptionObject,
  }
}
